/**
 * Law Firm Service
 *
 * Handles law firm data fetching for correspondence workflows
 * Extracted from workflow-data.service.ts for single responsibility
 */

import { getLawFirmProfileAction } from '@/lib/services/account/actions';

export interface LawFirmData {
  id: number;
  name: string;
  hasLetterhead: boolean;
  letterheadUrl: string | null;
  address: string;
  phone: string;
  email: string;
  emailSignature?: string;
  correspondencePreferences: {
    replyToOption: 'firm' | 'user' | 'generic';
    showUserContact: boolean;
    genericReplyEmail?: string;
    sendCopyToUser: boolean;
  };
}

/**
 * Get law firm data from profile
 * Fetches and transforms law firm profile data for correspondence workflows
 */
export async function getLawFirmData(): Promise<LawFirmData> {
  const defaultLawFirm: LawFirmData = {
    id: 1,
    name: 'Law Firm',
    hasLetterhead: false,
    letterheadUrl: null,
    address: '',
    phone: '',
    email: '',
    emailSignature: undefined,
    correspondencePreferences: {
      replyToOption: 'firm' as const,
      showUserContact: false,
      genericReplyEmail: undefined,
      sendCopyToUser: false
    }
  };

  try {
    const profileResult = await getLawFirmProfileAction();
    if (profileResult.success && profileResult.data) {
      const profile = profileResult.data;
      const letterheadUrl = profile.letterHeadFileId
        ? `/api/files/${profile.letterHeadFileId}/download?preview=true&image=true`
        : null;

      return {
        id: profile.id,
        name: profile.name,
        // The backend's own resolved capability, never `letterHeadFileId` (VALE-865)
        // — that is the PDF artefact, and `letterheadUrl` below is its only
        // correct consumer. This flag gates whether a letter can be sent at all.
        hasLetterhead: !!profile.canProduceLetterheadCorrespondence,
        letterheadUrl,
        address: profile.businessAddress
          ? `${profile.businessAddress.streetAddress || ''}, ${profile.businessAddress.suburb || ''} ${profile.businessAddress.state || ''} ${profile.businessAddress.postcode || ''}`.trim()
          : '',
        phone: profile.phoneNumber || '',
        email: profile.email || '',
        emailSignature: undefined,
        correspondencePreferences: profile.correspondencePreferences
          ? {
              replyToOption: profile.correspondencePreferences.replyToOption,
              showUserContact:
                profile.correspondencePreferences.showUserContact || false,
              genericReplyEmail:
                profile.correspondencePreferences.genericReplyEmail,
              sendCopyToUser: false // Default to false as it's not in the profile type
            }
          : defaultLawFirm.correspondencePreferences
      };
    }
  } catch (error) {
    console.error('Failed to fetch law firm profile:', error);
  }

  return defaultLawFirm;
}
